import React, { useCallback, useState } from "react";

import { Grid } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useTranslation } from "react-i18next";

import { PriceGroup } from "../../../../../models/price.group";
import { capitalizeFirstLetter } from "../../../../../util/capitalizeFirstLetter";
import { Button } from "../../../../Form";
import AutoSuggest from "../../../../Form/AutoSuggest";
import { Color } from "../../../../Form/Button";
import { Menu, MenuItem, Modal } from "./styles";

type Props = {
  groups: PriceGroup[];
  onAddBlankGroup: () => void;
  onAddExistingGroup: (groups: PriceGroup[]) => void;
};

export const NewGroupSection: React.FC<Props> = (props) => {
  const { groups, onAddBlankGroup, onAddExistingGroup } = props;

  const { t: translate } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isAddExistingGroupModalOpened, setAddExistingGroupModalOpened] = useState(false);

  const onClickToNewGroup = (event: HTMLElement) => {
    if (!!groups && !!groups.length) {
      setAnchorEl(event);
    } else {
      onAddBlankGroup();
    }
  };

  const onPressAndCloseAnchor = (fn: () => void) => {
    setAnchorEl(null);
    if (fn) {
      fn();
    }
  };

  const [groupsSelected, setGroupsSelected] = useState<PriceGroup[]>([]);

  const onOpenModalAddExistingGroup = useCallback(() => setAddExistingGroupModalOpened(true), []);
  const onCloseModalAddExistingGroup = useCallback(() => setAddExistingGroupModalOpened(false), []);
  const onSaveModalAddExistingGroup = () => {
    if (groupsSelected && groupsSelected.length) {
      onAddExistingGroup(groupsSelected);
      setGroupsSelected([]);
    }
    onCloseModalAddExistingGroup();
  };

  return (
    <React.Fragment>
      <Button
        customColor={Color.SECONDARY_OUTLINED}
        onClick={(event) => onClickToNewGroup(event.currentTarget)}
        endIcon={groups?.length ? <KeyboardArrowDownIcon /> : undefined}
      >
        {translate("newGroup")}
      </Button>

      <Menu
        open={!!anchorEl}
        id="new-group-menu"
        anchorEl={anchorEl}
        keepMounted
        onClose={() => setAnchorEl(null)}
        data-cy="new-group-menu"
      >
        <MenuItem
          onClick={() => onPressAndCloseAnchor(onAddBlankGroup)}
          data-cy="new-grou-menu-blank"
        >
          {translate("newBlankGroup")}
        </MenuItem>
        {!!groups.length && (
          <MenuItem
            data-cy="new-grou-menu-existing"
            onClick={() => onPressAndCloseAnchor(onOpenModalAddExistingGroup)}
          >
            {translate("addAnExistingGroup")}
          </MenuItem>
        )}
      </Menu>

      <Modal
        title={translate("addAnExistingGroup")}
        open={isAddExistingGroupModalOpened}
        handleClose={onCloseModalAddExistingGroup}
        showModalFooter
        showActionButtons
        actionButtonProps={{
          onCancel: onCloseModalAddExistingGroup,
          onSave: onSaveModalAddExistingGroup,
          showSaveButton: true,
          showCancelButton: true,
          textSave: translate("addGroup"),
        }}
      >
        <Grid container>
          <Grid item xs={12}>
            <AutoSuggest
              tagDataCy="group-existent-tag"
              data-cy="group-existent"
              label={capitalizeFirstLetter(translate("group"))}
              placeholder={`${translate("selectOneOrMoreGroups")}...`}
              options={groups}
              value={groupsSelected}
              getOptionLabel={(group: PriceGroup) => (group ? group.name || "" : "")}
              getOptionSelected={(option: PriceGroup, value: PriceGroup) => option.id === value.id}
              multiple
              onChange={(receivedGroups: PriceGroup[]) => setGroupsSelected(receivedGroups)}
            />
          </Grid>
        </Grid>
      </Modal>
    </React.Fragment>
  );
};
